import type { QueryClient } from "@tanstack/react-query";
import {
  SSE_ENTITY_QUERY_ROOT,
  SSE_EVENT_TYPES,
  SSE_STALE_AFTER_MS,
  sseEventSchema,
  type SseEvent,
} from "@salesboard/contracts";

export type ConnectionState = "connecting" | "live" | "reconnecting" | "stale";

/**
 * Which connection is the current one.
 *
 * Only one stream is meant to be open per session, so the newest call wins and every older
 * generation goes quiet. This is not defensive tidiness: React StrictMode mounts the effect
 * twice in development, and the torn-down first `EventSource` fires a late `error` that used
 * to write "reconnecting" over the live connection's state — the banner appearing on a board
 * whose stream was perfectly healthy. Real reconnect races have the same shape.
 */
let currentGeneration = 0;

/**
 * The live-update client.
 *
 * Events are invalidation hints, so this never applies data from the stream — it marks
 * queries stale and lets them re-read through the ordinary endpoints. Pushed state is
 * therefore identical to fetched state by construction.
 */
export function connectEvents(
  queryClient: QueryClient,
  options: {
    onStateChange?: (state: ConnectionState) => void;
    /** The signed-in user, so their own just-settled writes are not re-fetched. */
    currentUserId?: () => string | undefined;
  } = {},
): () => void {
  const generation = ++currentGeneration;
  const source = new EventSource("/api/events");
  let staleTimer: ReturnType<typeof setTimeout> | undefined;
  let disposed = false;

  /** Superseded or closed connections report nothing: their callbacks are ghosts. */
  const isCurrent = () => !disposed && generation === currentGeneration;

  const setState = (state: ConnectionState) => options.onStateChange?.(state);

  // No traffic — not even a heartbeat — for three intervals means what is on screen may
  // no longer be true. The board must never quietly show stale figures during a meeting.
  const armStaleTimer = () => {
    if (staleTimer) clearTimeout(staleTimer);
    staleTimer = setTimeout(() => {
      // A superseded connection's abandoned timer must not answer for the live one.
      if (isCurrent()) setState("stale");
    }, SSE_STALE_AFTER_MS);
  };

  setState("connecting");

  source.onopen = () => {
    if (!isCurrent()) return;
    setState("live");
    armStaleTimer();
    // Events missed while disconnected are unrecoverable individually, so re-read
    // everything rather than trying to reconcile.
    void queryClient.invalidateQueries();
  };

  source.onerror = () => {
    if (!isCurrent()) return;
    // EventSource reconnects on its own; this is a state change, not a failure to handle.
    setState("reconnecting");
  };

  source.onmessage = () => {
    // Unnamed messages only. The server frames everything it sends with an `event:` name,
    // so nothing arrives here today — and heartbeats certainly do not: the keep-alive is an
    // SSE *comment*, and `EventSource` discards comment lines without dispatching anything
    // at all. Believing they surfaced here as empty messages is what left the staleness
    // timer un-armed on every quiet board. The named `heartbeat` listener below is the one
    // that keeps it alive; this handler stays only so an unnamed frame would still count.
    if (!isCurrent()) return;
    armStaleTimer();
    setState("live");
  };

  /**
   * Every event type in the contract, iterated — never a list written out here.
   *
   * The server frames each event with a `event:` name, and `EventSource` **silently drops**
   * a named event nobody registered a listener for: `onmessage` only ever sees unnamed
   * ones. So a type added to the contract and forgotten here does not fail, or warn, or
   * appear in a console — it simply stops arriving, and the feature that depends on it
   * looks like it works because the acting user's own screen updates from its mutation.
   *
   * That is not hypothetical: `stage.updated`, `setting.updated` and `owner.updated` were
   * all added to `SSE_EVENT_TYPES` and all missing from the literal list that used to be
   * here, so three increments' worth of live propagation was dead on arrival.
   */
  for (const type of Object.values(SSE_EVENT_TYPES)) {
    source.addEventListener(type, (event) => {
      if (!isCurrent()) return;
      armStaleTimer();
      setState("live");

      // The heartbeat is liveness and nothing else: it names no record, its payload is `{}`,
      // and re-arming the timer above is its entire job. Every other type is a hint.
      if (type === SSE_EVENT_TYPES.heartbeat) return;

      const parsed = sseEventSchema.safeParse(JSON.parse((event as MessageEvent<string>).data));
      if (!parsed.success) return;

      invalidateFor(queryClient, parsed.data, options.currentUserId?.());
    });
  }

  return () => {
    disposed = true;
    if (staleTimer) clearTimeout(staleTimer);
    source.close();
  };
}

/**
 * Map an event to the queries it invalidates.
 *
 * The predicate is the important part, and it is a fix for a real bug rather than
 * tidiness: a user who saves a record receives their own event back, and invalidating
 * the detail query they are looking at re-fetches what they already have and flickers
 * the form. Their own writes therefore invalidate lists but not the detail they are on.
 * Do not "simplify" this away — see `.claude/docs/realtime.md`.
 */
function invalidateFor(queryClient: QueryClient, event: SseEvent, currentUserId: string | undefined): void {
  queryClient.invalidateQueries({ predicate: (query) => shouldInvalidate(query.queryKey, event, currentUserId) });
}

/**
 * Whether one query key is affected by one event. Exported because it is pure, and because
 * it is where the mistake was: nothing about a wrong answer here is visible on screen —
 * updates simply stop arriving. See `sse.test.ts`.
 */
export function shouldInvalidate(key: readonly unknown[], event: SseEvent, currentUserId: string | undefined): boolean {
  const root = typeof key[0] === "string" ? key[0] : undefined;
  // Several roots per entity: configuration reaches beyond its own screen — a renamed stage
  // changes what the board renders, a changed threshold changes which cards read as stalled.
  const roots: readonly string[] = SSE_ENTITY_QUERY_ROOT[event.entity];
  if (root === undefined || (!roots.includes(root) && root !== event.entity)) return false;

  const isOwnWrite = Boolean(event.actorId && currentUserId && event.actorId === currentUserId);
  const isDetailOfThisRecord = key[1] === event.id;
  if (isDetailOfThisRecord && isOwnWrite) return false;

  return true;
}
